<template>
  <div class="analytic-plan-rules-manager">
    <data-table-with-api
      ref="analyticPlansByReferenceTable"
      :headers="headers"
      :url="`analytic_plans_by_reference?companydb_id=${companydbId}`"
      :footer-items-per-page-text="itemsPerPageText"
      :search-filters="searchFilters"
      :showSelect="true"
      :show-btn-list="!hideCreateButton"
      :action-btn-list="actionBtnList"
      @click-col-action-btn="clickActionBtn"
      @selected-items-changed="selectedItemsChanged"
    >
      <!-- right buttons of search -->
      <template slot="search_right">
        <div align="right">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="refreshList"
                color="primary"
                icon
                v-bind="attrs"
                v-on="on"
                :loading="loading"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualizar</span>
          </v-tooltip>
          <v-tooltip top v-if="!hideCreateButton">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="openRuleDialog()"
                color="primary"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Nueva Regla de Plan Analítico</span>
          </v-tooltip>
          <v-tooltip top v-if="!hideCreateButton && selectedRules.length > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="openDeleteRuleDialog()"
                color="primary"
                v-bind="attrs"
                v-on="on"
                icon
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Eliminar Regla</span>
          </v-tooltip>
        </div>
      </template>
    </data-table-with-api>

    <!-- dialog to create/edit rule -->
    <app-modal-prompt
      v-model="ruleDialog"
      :loading="ruleDialogLoading"
      @confirm="confirmRule()"
      @cancel="cancelRule()"
    >
      <template #title>
        {{ ruleToUpdate ? 'Editar' : 'Nueva' }} Regla de Plan Analítico
      </template>
      <v-form ref="ruleDialogForm">
        <v-radio-group
          v-model="newRule.third_party_type"
          row
          label="Tipo de tercero"
        >
          <v-radio
            label="Cliente"
            value="customer"
          ></v-radio>
          <v-radio
            label="Proveedor"
            value="supplier"
          ></v-radio>
        </v-radio-group>
        <v-combobox
          v-if="newRule.third_party_type"
          v-model="newRule.third_party_account"
          label="Cuenta del tercero"
          :items="thirdPartyAccountOptions"
          item-value="id"
          item-text="name"
          :hint="newRule.third_party_type === 'supplier' ? 'Escribe para buscar proveedor' : 'Escribe para buscar cliente'"
          persistent-hint
          :loading="thirdPartyLoading"
          :filter="filterThirdPartyBy"
          @input.native="onThirdPartyNativeInput"
          @change="onThirdPartyAccountChange"
          @blur="resetThirdPartyItems"
          clearable
        >
          <template slot="item" slot-scope="data">
            {{ data.item.account }} - {{ data.item.name }}
          </template>
        </v-combobox>
        <v-combobox
          v-model="newRule.accounts"
          label="Cuentas contables"
          :items="accountOptions"
          item-text="account"
          item-value="account"
          multiple
          chips
          deletable-chips
          small-chips
          hint="Escribe para buscar cuentas. Puedes seleccionar múltiples cuentas."
          persistent-hint
          :rules="[validationRules.required]"
          :filter="filterAccountBy"
          @input.native="onAccountNativeInput"
          @blur="resetAccountItems"
        >
          <template slot="item" slot-scope="data">
            {{ data.item.account }} - {{ data.item.description }}
          </template>
        </v-combobox>
        <app-autocomplete
          v-model="newRule.plan_code"
          label="Plan Analítico"
          :items="analyticPlansOptions"
          item-value="code"
          item-text="name"
          :rules="[validationRules.required]"
          @change="onPlanCodeChange"
          class="mt-5"
        />
        <app-autocomplete
          v-if="newRule.plan_code"
          v-model="newRule.level1_code"
          label="Nivel 1"
          :items="level1Options"
          item-value="code"
          item-text="name"
          :rules="[validationRules.required]"
          @change="onLevel1Change"
        />
        <app-autocomplete
          v-if="newRule.level1_code && level2Options.length > 0"
          v-model="newRule.level2_code"
          label="Nivel 2 (opcional)"
          :items="level2Options"
          item-value="code"
          item-text="name"
          clearable
        />
        <v-text-field
          v-model="newRule.reference"
          label="Referencia"
          hint="Referencia del tercero"
          persistent-hint
        />
      </v-form>
    </app-modal-prompt>

    <!-- dialog to confirm deletion -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>¿Eliminar regla(s)?</v-card-title>
        <v-card-text>
          Se eliminarán {{ selectedRules.length }} regla(s) de planes analíticos.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" text @click="confirmDelete" :loading="deleteLoading">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataTableWithApi from '@/components/DataTableWithApi.vue';
import AppModalPrompt from '@/components/layout/AppModalPrompt.vue';
import AppAutocomplete from '@/components/layout/AppAutocomplete.vue';
import { sync_command_dispatcher, client } from '@/api/client';
import { CreateAnalyticPlanRule } from '../application/CreateAnalyticPlanRule';
import { UpdateAnalyticPlanRule } from '../application/UpdateAnalyticPlanRule';
import { DeleteAnalyticPlanRules } from '../application/DeleteAnalyticPlanRules';
import { SearchThirdsQuery } from '../application/SearchThirdsQuery';
import { ApiThirdRepository } from './ApiThirdRepository';

export default {
  name: 'AnalyticPlanRulesManager',
  components: {
    DataTableWithApi,
    AppModalPrompt,
    AppAutocomplete
  },
  props: {
    companydbId: {
      type: Number,
      required: true
    },
    userId: {
      type: Number,
      required: false,
      default: null
    },
    hideCreateButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      analyticPlans: null,
      ruleDialog: false,
      ruleDialogLoading: false,
      deleteDialog: false,
      deleteLoading: false,
      ruleToUpdate: null,
      selectedRules: [],
      allSelected: false,
      level1Options: [],
      level2Options: [],
      thirdPartyAccountOptions: [],
      thirdPartySearchDebounce: null,
      thirdPartyLoading: false,
      thirdPartyAbortController: null,
      thirdPartyLastSearch: '',
      accountOptions: [],
      accountSearchDebounce: null,
      searchThirdsQuery: new SearchThirdsQuery(new ApiThirdRepository()),
      newRule: {
        third_party_type: null,
        third_party_account: null,
        reference: null,
        accounts: [],
        plan_code: null,
        level1_code: null,
        level2_code: null
      },
      validationRules: {
        required: v => !!v || 'Campo requerido'
      },
      itemsPerPageText: 'Reglas por página:',
      searchFilters: ['text__icontains'],
      actionBtnList: [
        {
          name: 'Editar Regla',
          action: 'update_analytic_rule',
          icon: 'mdi-pencil-outline'
        }
      ]
    };
  },
  computed: {
    headers() {
      return [
        {
          text: 'Cuentas',
          sortable: true,
          value: 'accounts_display'
        },
        {
          text: 'Plan Analítico',
          value: 'plan_name',
          sortable: true
        },
        {
          text: 'Nivel 1',
          value: 'level1_name',
          sortable: true
        },
        {
          text: 'Nivel 2',
          value: 'level2_name',
          sortable: false
        },
        {
          text: '',
          align: 'end',
          value: 'actions',
          sortable: false
        }
      ];
    },
    analyticPlansOptions() {
      if (!this.analyticPlans) {
        return [];
      }

      // first try with 'information', then with 'analytic_plans'
      const plansArray = this.analyticPlans.information || this.analyticPlans.analytic_plans;

      if (!plansArray || !Array.isArray(plansArray) || plansArray.length === 0) {
        return [];
      }

      return plansArray.map(plan => ({
        code: plan.code,
        name: plan.name,
        levels: plan.levels || []
      }));
    }
  },
  methods: {
    openRuleDialog() {
      if (this.$refs.ruleDialogForm) {
        this.$refs.ruleDialogForm.reset();
      }
      this.resetRule();
      this.ruleDialog = true;
    },

    resetRule() {
      this.newRule = {
        third_party_type: null,
        third_party_account: null,
        reference: null,
        accounts: [],
        plan_code: null,
        level1_code: null,
        level2_code: null
      };
      this.ruleToUpdate = null;
      this.level1Options = [];
      this.level2Options = [];
      this.thirdPartyAccountOptions = [];
      this.thirdPartySearch = null;
      this.thirdPartyPage = 1;
      this.thirdPartyHasMore = true;
    },

    cancelRule() {
      this.ruleDialog = false;
      this.resetRule();
    },

    confirmRule() {
      if (this.ruleToUpdate) {
        this.updateRule();
      } else {
        this.createRule();
      }
    },

    onThirdPartyAccountChange(value) {
      let thirdParty = null;

      if (typeof value === 'object' && value !== null) {
        thirdParty = value;
      } else {
        thirdParty = this.thirdPartyAccountOptions.find(item => item.id === value);
      }

      if (!thirdParty) {
        return;
      }

      if (!Array.isArray(this.newRule.accounts)) {
        this.newRule.accounts = [];
      }

      if (thirdParty.account && !this.newRule.accounts.includes(thirdParty.account)) {
        this.newRule.accounts.push(thirdParty.account);
      }

      if (this.newRule.third_party_type === 'supplier' && thirdParty.expens_account) {
        if (!this.newRule.accounts.includes(thirdParty.expens_account)) {
          this.newRule.accounts.push(thirdParty.expens_account);
        }
      } else if (this.newRule.third_party_type === 'customer' && thirdParty.income_account) {
        if (!this.newRule.accounts.includes(thirdParty.income_account)) {
          this.newRule.accounts.push(thirdParty.income_account);
        }
      }
    },

    onPlanCodeChange(planCode) {
      this.newRule.level1_code = null;
      this.newRule.level2_code = null;
      this.level2Options = [];

      const selectedPlan = this.analyticPlansOptions.find(p => p.code === planCode);
      if (selectedPlan) {
        this.level1Options = selectedPlan.levels || [];
      } else {
        this.level1Options = [];
      }
    },

    onLevel1Change(level1Code) {
      this.newRule.level2_code = null;

      const selectedLevel1 = this.level1Options.find(l => l.code === level1Code);
      if (selectedLevel1) {
        this.level2Options = selectedLevel1.levels || [];
      } else {
        this.level2Options = [];
      }
    },

    normalizeAccounts(accounts) {
      // normalize accounts: convert objects to strings (account codes)
      return accounts.map(acc => {
        if (typeof acc === 'string') {
          return acc;
        } else if (acc && typeof acc === 'object' && acc.account) {
          return acc.account;
        }
        return null;
      }).filter(acc => acc !== null);
    },

    async createRule() {
      this.ruleDialogLoading = true;
      const formIsValid = this.$refs.ruleDialogForm.validate();

      if (formIsValid) {
        try {
          const normalizedAccounts = this.normalizeAccounts(this.newRule.accounts);

          const thirdPartyId = this.newRule.third_party_account && typeof this.newRule.third_party_account === 'object'
            ? this.newRule.third_party_account.id
            : this.newRule.third_party_account;

          await new CreateAnalyticPlanRule(sync_command_dispatcher).run(
            this.companydbId,
            normalizedAccounts,
            this.newRule.plan_code,
            this.newRule.level1_code,
            this.newRule.level2_code,
            this.newRule.third_party_type,
            thirdPartyId,
            this.newRule.reference
          );
          this.ruleDialogLoading = false;
          this.ruleDialog = false;
          this.resetRule();
          this.refreshList();
          console.log('Regla creada correctamente');
        } catch (error) {
          console.error('Error al crear la regla:', error);
          this.ruleDialogLoading = false;
        }
      } else {
        this.ruleDialogLoading = false;
        console.error('Formulario inválido');
      }
    },

    async updateRule() {
      this.ruleDialogLoading = true;
      const formIsValid = this.$refs.ruleDialogForm.validate();

      if (formIsValid) {
        const normalizedAccounts = this.normalizeAccounts(this.newRule.accounts);

        const thirdPartyId = this.newRule.third_party_account && typeof this.newRule.third_party_account === 'object'
          ? this.newRule.third_party_account.id
          : this.newRule.third_party_account;

        const params = {
          accounts: normalizedAccounts,
          plan_code: this.newRule.plan_code,
          level1_code: this.newRule.level1_code,
          level2_code: this.newRule.level2_code,
          supplierdb: this.newRule.third_party_type === 'supplier' && thirdPartyId ? thirdPartyId : null,
          customerdb: this.newRule.third_party_type === 'customer' && thirdPartyId ? thirdPartyId : null,
          reference: this.newRule.reference
        };

        try {
          await new UpdateAnalyticPlanRule(sync_command_dispatcher).run(
            this.ruleToUpdate.id,
            params
          );
          this.ruleDialogLoading = false;
          this.ruleDialog = false;
          this.resetRule();
          this.refreshList();
          console.log('Regla actualizada correctamente');
        } catch (error) {
          console.error('Error al actualizar la regla:', error);
          this.ruleDialogLoading = false;
        }
      } else {
        this.ruleDialogLoading = false;
        console.error('Formulario inválido');
      }
    },

    async clickActionBtn(action, item) {
      if (action === 'update_analytic_rule') {
        this.ruleToUpdate = item;

        if (item.supplierdb) {
          this.newRule.third_party_type = 'supplier';
          const supplierData = await this.fetchThirdPartyById(item.supplierdb, 'supplier');
          if (supplierData) {
            this.newRule.third_party_account = supplierData;
            this.thirdPartyAccountOptions = [supplierData];
          } else {
            this.newRule.third_party_account = item.supplierdb;
          }
        } else if (item.customerdb) {
          this.newRule.third_party_type = 'customer';
          // load full customer data
          const customerData = await this.fetchThirdPartyById(item.customerdb, 'customer');
          if (customerData) {
            this.newRule.third_party_account = customerData;
            this.thirdPartyAccountOptions = [customerData];
          } else {
            this.newRule.third_party_account = item.customerdb;
          }
        } else {
          this.newRule.third_party_type = null;
          this.newRule.third_party_account = null;
        }

        this.newRule.reference = item.reference || null;
        this.newRule.accounts = item.accounts ? [...item.accounts] : [];
        this.newRule.plan_code = item.plan_code;

        // load level 1 options
        this.onPlanCodeChange(item.plan_code);

        // after loading level 1, set the value
        this.$nextTick(() => {
          this.newRule.level1_code = item.level1_code;

          // load level 2 options
          if (item.level1_code) {
            this.onLevel1Change(item.level1_code);

            // after loading level 2, set the value
            this.$nextTick(() => {
              this.newRule.level2_code = item.level2_code;
            });
          }
        });

        this.ruleDialog = true;
      }
    },

    selectedItemsChanged(selected, allSelected) {
      this.selectedRules = selected;
      this.allSelected = allSelected;
    },

    refreshList() {
      if (this.$refs.analyticPlansByReferenceTable) {
        this.loading = true;
        this.$refs.analyticPlansByReferenceTable.getItems()
          .then(() => {
            this.loading = false;
          })
          .catch(err => {
            console.error(err);
            this.loading = false;
          });
      }
    },

    openDeleteRuleDialog() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteLoading = true;

      try {
        await new DeleteAnalyticPlanRules(sync_command_dispatcher).run(
          this.selectedRules.map(r => r.id),
          this.companydbId
        );
        this.deleteLoading = false;
        this.deleteDialog = false;
        this.refreshList();
        console.log('Reglas eliminadas correctamente');
      } catch (error) {
        console.error('Error al eliminar las reglas:', error);
        this.deleteLoading = false;
      }
    },

    async fetchAnalyticPlans() {
      try {
        const response = await client.get('analytic_plans/', {
          params: { companydb_id: this.companydbId }
        });

        // the backend returns a paginated response with results
        const results = response.data.results || response.data;

        if (results && results.length > 0) {
          this.analyticPlans = results[0];
        }
      } catch (error) {
        console.error('Error fetching analytic plans:', error);
      }
    },

    async fetchThirdPartyById(id, type) {
      try {
        if (!id || !type) {
          return null;
        }

        const endpoint = type === 'supplier' ? 'suppliers/' : 'customers/';
        const url = `${endpoint}${id}/?companydb_id=${this.companydbId}`;
        const response = await client.get(url);
        const data = response.data;

        return type === 'supplier'
          ? {
              id: data.id,
              name: data.first_name || data.name || '',
              account: data.credit_account,
              expens_accounts: data.expens_accounts || []
            }
          : {
              id: data.id,
              name: data.first_name || data.name || '',
              account: data.debit_account,
              income_accounts: data.income_accounts || []
            };
      } catch (error) {
        console.error('Error fetching third party by id:', error);
        return null;
      }
    },

    async fetchThirdPartyAccounts(searchText = '') {
      try {
        if (!this.newRule.third_party_type) {
          this.thirdPartyAccountOptions = [];
          return;
        }

        if (!searchText || searchText.trim() === '') {
          this.thirdPartyAccountOptions = [];
          return;
        }

        if (searchText.trim().length < 3) {
          return;
        }

        if (searchText.trim() === this.thirdPartyLastSearch) {
          return;
        }

        if (this.thirdPartyAbortController) {
          this.thirdPartyAbortController.abort();
        }

        this.thirdPartyLastSearch = searchText.trim();
        this.thirdPartyLoading = true;
        this.thirdPartyAbortController = new AbortController();

        if (!this.userId) {
          console.error('User ID not found');
          this.thirdPartyLoading = false;
          return;
        }

        const results = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: searchText.trim(),
          thirdType: this.newRule.third_party_type,
          limit: 10
        });

        this.thirdPartyAccountOptions = results;

        this.thirdPartyLoading = false;
        this.thirdPartyAbortController = null;
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'canceled') {
          // request was cancelled, this is expected
        } else {
          console.error('Error fetching third party accounts:', error);
        }
        this.thirdPartyLoading = false;
        this.thirdPartyAbortController = null;
      }
    },

    async fetchAccounts(searchText = '') {
      try {
        if (!searchText || searchText.trim() === '') {
          this.accountOptions = [];
          return;
        }

        const url = `accounts_all/?companydb=${this.companydbId}&text__icontains=${searchText}`;
        const response = await client.get(url);
        const results = response.data.results || response.data;

        this.accountOptions = results.map(acc => ({
          account: acc.account || acc,
          description: acc.description || ''
        }));
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    },

    onAccountNativeInput(event) {
      const searchText = event.target.value;

      if (this.accountSearchDebounce) {
        clearTimeout(this.accountSearchDebounce);
      }

      this.accountSearchDebounce = setTimeout(() => {
        this.fetchAccounts(searchText);
      }, 300);
    },

    resetAccountItems() {
      this.accountOptions = [];
    },

    onThirdPartyNativeInput(event) {
      const searchText = event.target.value;

      if (this.thirdPartySearchDebounce) {
        clearTimeout(this.thirdPartySearchDebounce);
      }

      this.thirdPartySearchDebounce = setTimeout(() => {
        this.fetchThirdPartyAccounts(searchText);
      }, 500);
    },

    resetThirdPartyItems() {
      // cancel any pending request
      if (this.thirdPartyAbortController) {
        this.thirdPartyAbortController.abort();
        this.thirdPartyAbortController = null;
      }

      // clear debounce
      if (this.thirdPartySearchDebounce) {
        clearTimeout(this.thirdPartySearchDebounce);
        this.thirdPartySearchDebounce = null;
      }

      this.thirdPartyAccountOptions = [];
      this.thirdPartyLastSearch = '';
    },

    filterThirdPartyBy(item, queryText) {
      if (!item || !item.name) return false;
      const text = (item.account || '').toLowerCase() + ' - ' + item.name.toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    },

    filterAccountBy(item, queryText) {
      if (!item || !item.account) return false;
      const text = item.account.toLowerCase() + ' - ' + (item.description || '').toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    }
  },

  watch: {
    'newRule.third_party_type'() {
      this.thirdPartyAccountOptions = [];
    }
  },

  mounted() {
    this.fetchAnalyticPlans();
  }
};
</script>

<style scoped>
.analytic-plan-rules-manager {
  width: 100%;
}
</style>
